import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getExternalWeatherByCoordinates,
  reverseGeocode,
} from '../api.js';
import {
  getBaiduBrowserPosition,
  getBaiduLocalCity,
} from '../utils/baiduGeolocation.js';

export const LOCATION_CACHE_KEY = 'plantai_last_location';
const LOCATION_CACHE_MAX_AGE = 24 * 60 * 60 * 1000;
const LOCATE_HARD_TIMEOUT_MS = 16000;

const DEFAULT_COORDS = { latitude: 39.9042, longitude: 116.4074 };
const DEFAULT_ADDRESS = {
  province: '北京市',
  city: '北京市',
  district: '朝阳区',
  township: '',
  street: '',
  streetNumber: '',
  formattedAddress: '北京市朝阳区',
  source: '默认地址',
};
const DEFAULT_WEATHER = {
  text: '',
  temp: null,
  tempMin: null,
  tempMax: null,
  windDir: '',
  humidity: null,
  pressure: null,
  feelsLike: null,
  windSpeed: null,
  sunrise: null,
  sunset: null,
  source: '无数据',
};

export function loadCachedLocation() {
  try {
    const raw = localStorage.getItem(LOCATION_CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (!cached?.coords?.latitude || !cached?.coords?.longitude) return null;
    if (Date.now() - (cached.timestamp || 0) > LOCATION_CACHE_MAX_AGE) return null;
    return cached;
  } catch {
    return null;
  }
}

/**
 * @param coords
 * @param addressInfo
 * @param weatherData
 * @param {'auto'|'cache'|'gps'} source
 */
export function saveCachedLocation(coords, addressInfo, weatherData, source = 'auto') {
  try {
    const normalizedSource =
      source === 'manual' || source === 'ip' ? 'auto' : source || 'auto';
    localStorage.setItem(
      LOCATION_CACHE_KEY,
      JSON.stringify({
        coords: {
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracy: coords.accuracy ?? null,
        },
        address: addressInfo || {},
        weather: weatherData || {},
        timestamp: Date.now(),
        source: normalizedSource,
      })
    );
  } catch {
    /* ignore */
  }
}

/** @deprecated 手动定位已移除，保留导出名避免其它文件引用报错 */
export function clearManualLocationCacheFlag() {
  try {
    const raw = localStorage.getItem(LOCATION_CACHE_KEY);
    if (!raw) return;
    const cached = JSON.parse(raw);
    if (cached?.source !== 'manual' && cached?.source !== 'ip') return;
    cached.source = 'auto';
    cached.timestamp = Date.now();
    localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(cached));
  } catch {
    /* ignore */
  }
}

function normalizeAddress(addr) {
  if (!addr || typeof addr !== 'object') return null;
  const province = addr.province || '';
  const city = addr.city && addr.city !== '[]' ? addr.city : province || '';
  const district = addr.district || '';
  if (!province && !city && !district) return null;
  return {
    province,
    city,
    district,
    township: addr.township || '',
    street: addr.street || '',
    streetNumber: addr.streetNumber || '',
    formattedAddress:
      addr.formattedAddress ||
      [province, city, district, addr.street || '', addr.streetNumber || ''].filter(Boolean).join(''),
    source: addr.source || '地址解析',
  };
}

async function getAddressFromCoords(latitude, longitude, preferred) {
  const fromPreferred = normalizeAddress(preferred);
  if (fromPreferred?.city || fromPreferred?.province) return fromPreferred;

  try {
    const response = await reverseGeocode(latitude, longitude);
    const data = response?.data;
    if (data?.success && data.data) {
      const normalized = normalizeAddress({
        ...data.data,
        source: '后端代理',
      });
      if (normalized) return normalized;
    }
  } catch (e) {
    console.warn('逆地理编码失败:', e);
  }

  return {
    ...DEFAULT_ADDRESS,
    source: '默认地址（逆地理失败）',
  };
}

async function getWeather(latitude, longitude) {
  try {
    const response = await getExternalWeatherByCoordinates(latitude, longitude);
    const data = response?.data;

    if (data?.success && data.data) {
      const weatherData = data.data;
      return {
        text: weatherData.description || '',
        temp: weatherData.temperature ?? null,
        tempMin: weatherData.tempMin ?? null,
        tempMax: weatherData.tempMax ?? null,
        windDir: weatherData.windDirection || '',
        humidity: weatherData.humidity ?? null,
        pressure: weatherData.pressure ?? null,
        feelsLike: weatherData.feelsLike ?? null,
        windSpeed: weatherData.windSpeed ?? null,
        sunrise: weatherData.sunrise || null,
        sunset: weatherData.sunset || null,
        source: '后端API',
      };
    }

    return { ...DEFAULT_WEATHER };
  } catch (error) {
    console.warn('获取天气数据失败:', error);
    return { ...DEFAULT_WEATHER, source: '无数据（API失败）' };
  }
}

async function locateWithBaidu(opts = {}) {
  try {
    return await getBaiduBrowserPosition({
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
      enableSDKLocation: true,
      loadTimeoutMs: 15000,
      html5Promise: opts.html5Promise,
    });
  } catch (primaryErr) {
    if (primaryErr?.permissionDenied) throw primaryErr;
    if (opts.force) throw primaryErr;
    try {
      return await getBaiduLocalCity({ loadTimeoutMs: 10000 });
    } catch {
      throw primaryErr;
    }
  }
}

export default function useLocationWeather() {
  const [location, setLocation] = useState(null);
  const [address, setAddress] = useState({});
  const [weather, setWeather] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [status, setStatus] = useState('locating');
  const [statusMessage, setStatusMessage] = useState('正在准备位置…');
  const requestIdRef = useRef(0);
  const bootstrappedRef = useRef(false);

  const applyResolved = useCallback(async (coords, opts = {}) => {
    const {
      preferredAddress = null,
      source = 'auto',
      isDefault = false,
      keepLoading = false,
      statusReadyMsg = null,
    } = opts;

    setLocation({
      latitude: coords.latitude,
      longitude: coords.longitude,
      accuracy: coords.accuracy ?? null,
      isDefault: Boolean(isDefault),
      source: coords.source || source,
    });

    const addressInfo = await getAddressFromCoords(
      coords.latitude,
      coords.longitude,
      preferredAddress || coords.address
    );
    setAddress(addressInfo);

    const weatherData = await getWeather(coords.latitude, coords.longitude);
    setWeather(weatherData);

    if (!isDefault) {
      const cacheSrc = source === 'cache' ? 'cache' : 'auto';
      saveCachedLocation(coords, addressInfo, weatherData, cacheSrc);
    }

    if (!keepLoading) {
      setLoading(false);
      setStatus('ready');
      setStatusMessage(statusReadyMsg || (isDefault ? '已使用备用位置' : '定位成功'));
    }

    return { addressInfo, weatherData };
  }, []);

  /**
   * @param {{ force?: boolean, html5Promise?: Promise<object> }} [options]
   */
  const fetchLocationData = useCallback(async (options = {}) => {
    const force = Boolean(options?.force);
    const html5Promise = options?.html5Promise;
    const reqId = ++requestIdRef.current;

    setLoading(true);
    setError(null);
    setPermissionDenied(false);
    setStatus('locating');
    setStatusMessage('正在定位…');

    const cached = loadCachedLocation();
    const locatePromise = locateWithBaidu({ force, html5Promise });

    let hardTimedOut = false;
    const hardTimer = setTimeout(() => {
      hardTimedOut = true;
      if (reqId !== requestIdRef.current) return;
      setLoading(false);
      setStatus('failed');
      setStatusMessage('定位超时');
      setError(new Error('定位超时'));
    }, LOCATE_HARD_TIMEOUT_MS);

    try {
      const coords = await locatePromise;
      if (reqId !== requestIdRef.current || hardTimedOut) return;

      setError(null);
      setPermissionDenied(false);
      await applyResolved(coords, {
        preferredAddress: coords.address,
        source: 'auto',
        isDefault: false,
        statusReadyMsg: '定位成功',
      });
    } catch (err) {
      if (reqId !== requestIdRef.current) return;

      const denied = Boolean(err?.permissionDenied);
      const message = denied
        ? '定位权限被拒绝'
        : (err?.message || '定位失败');

      setPermissionDenied(denied);
      setError(new Error(message));
      setStatus(denied ? 'denied' : 'failed');
      setStatusMessage(message);
      setLoading(false);

      // 无可用缓存时交给页面侧主农场坐标兜底，不再走 IP 粗定位
      if (!cached?.coords || cached?.source === 'ip') {
        setLocation({ ...DEFAULT_COORDS, isDefault: true, source: 'default' });
        setAddress(DEFAULT_ADDRESS);
        setWeather(DEFAULT_WEATHER);
      }
    } finally {
      clearTimeout(hardTimer);
    }
  }, [applyResolved]);

  /** 首屏先刷缓存避免空白，同时立刻发起天地图定位以弹出浏览器授权 */
  useEffect(() => {
    if (bootstrappedRef.current) return undefined;
    bootstrappedRef.current = true;

    const cached = loadCachedLocation();
    // 历史 IP 粗定位缓存不再使用
    if (cached?.coords && cached?.source !== 'ip') {
      applyResolved(cached.coords, {
        preferredAddress: cached.address,
        source: 'cache',
        isDefault: false,
        keepLoading: true,
        statusReadyMsg: '已使用缓存位置',
      }).catch(() => {});
    } else {
      setStatus('locating');
      setStatusMessage('正在定位…');
    }

    fetchLocationData();
    return undefined;
  }, [applyResolved, fetchLocationData]);

  const refetch = useCallback((options) => {
    return fetchLocationData({ force: true, ...(options || {}) });
  }, [fetchLocationData]);

  return {
    location,
    address,
    weather,
    loading,
    error,
    permissionDenied,
    status,
    statusMessage,
    refetch,
  };
}
